import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useClient } from '../context/ClientContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Plus, Trash2, RefreshCw, Sparkles, Target, Loader2, Pencil, X, AlertTriangle, Search, CheckCircle2, FileUp, BookOpen } from 'lucide-react';

const PRICE_LEVELS = ['₹', '₹₹', '₹₹₹', '₹₹₹₹'];

const EMPTY_FORM = {
  name: '', cuisine: '', address: '',
  rating: '', review_count: '', price_level: '',
  avg_dish_price: '', popular_dishes: '',
  google_place_id: '', notes: '',
  menu_items: [],
};

export const Benchmarks = () => {
  const { selectedClient, API } = useClient();
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [myStats, setMyStats] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [positioning, setPositioning] = useState(null);
  const [loadingPositioning, setLoadingPositioning] = useState(false);

  // Places search (typeahead)
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [placesStatus, setPlacesStatus] = useState('unknown'); // 'unknown' | 'ok' | 'not_configured' | 'api_disabled'
  const [placesError, setPlacesError] = useState(null);
  const searchTimer = useRef(null);

  // Menu extraction state (inside Add dialog)
  const [menuExtracting, setMenuExtracting] = useState(false);
  const [menuExtractError, setMenuExtractError] = useState(null);
  const [menuExtractResult, setMenuExtractResult] = useState(null);
  const menuFileInputRef = useRef(null);

  const fetchAll = async () => {
    if (!selectedClient) return;
    setLoading(true);
    setLoadingPositioning(true);
    try {
      const [cs, an, pos] = await Promise.all([
        axios.get(`${API}/benchmarks/${selectedClient.id}/competitors`),
        axios.get(`${API}/benchmarks/${selectedClient.id}/analysis`),
        axios.get(`${API}/benchmarks/${selectedClient.id}/positioning`),
      ]);
      setCompetitors(cs.data.competitors || []);
      if (an.data?.analysis) {
        setAnalysis(an.data.analysis);
        setMyStats(an.data.my_stats || null);
      }
      setPositioning(pos.data || null);
    } catch (_) { /* silent */ }
    finally { setLoading(false); setLoadingPositioning(false); }
  };

  useEffect(() => { fetchAll(); }, [selectedClient]);

  // Probe Places API status once on mount (cheap call with q='restaurant') so
  // user immediately sees the green/red/amber banner. Cancellation flag
  // prevents stale setState if the component unmounts mid-flight.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/benchmarks/places/search`, { params: { q: 'restaurant' } });
        if (cancelled) return;
        if (res.data.success) setPlacesStatus('ok');
        else if (res.data.reason === 'not_configured') setPlacesStatus('not_configured');
        else { setPlacesStatus('api_disabled'); setPlacesError(res.data.error_detail || res.data.message); }
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [API]);

  // Debounced Places search — runs whenever searchQ changes inside the Add dialog
  const runPlacesSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    setPlacesError(null);
    try {
      const res = await axios.get(`${API}/benchmarks/places/search`, { params: { q: query } });
      if (res.data.success) {
        setSearchResults(res.data.results || []);
        setPlacesStatus('ok');
      } else {
        setSearchResults([]);
        if (res.data.reason === 'not_configured') {
          setPlacesStatus('not_configured');
        } else {
          setPlacesStatus('api_disabled');
          setPlacesError(res.data.error_detail || res.data.message);
        }
      }
    } catch (e) {
      setSearchResults([]);
      setPlacesError(e?.response?.data?.detail || e.message);
    } finally {
      setSearching(false);
    }
  }, [API]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => runPlacesSearch(searchQ), 350);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [searchQ, runPlacesSearch]);

  const pickPlace = (p) => {
    setForm({
      ...form,
      name: p.name || form.name,
      address: p.address || form.address,
      rating: p.rating ?? form.rating ?? '',
      review_count: p.review_count ?? form.review_count ?? '',
      price_level: p.price_level || form.price_level,
      google_place_id: p.place_id || form.google_place_id,
    });
    setSearchResults([]);
    setSearchQ('');
  };

  const startAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setMenuExtractResult(null);
    setMenuExtractError(null);
    setShowForm(true);
  };
  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || '',
      cuisine: c.cuisine || '',
      address: c.address || '',
      rating: c.rating ?? '',
      review_count: c.review_count ?? '',
      price_level: c.price_level || '',
      avg_dish_price: c.avg_dish_price ?? '',
      popular_dishes: (c.popular_dishes || []).join(', '),
      google_place_id: c.google_place_id || '',
      notes: c.notes || '',
      menu_items: c.menu_items || [],
    });
    setMenuExtractResult(null);
    setMenuExtractError(null);
    setShowForm(true);
  };

  // Menu extraction — uploads PDF/image/Excel, calls AI extractor, populates form
  const handleMenuFile = async (file) => {
    if (!file) return;
    setMenuExtracting(true);
    setMenuExtractError(null);
    setMenuExtractResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/benchmarks/competitors/extract-menu`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!res.data.success || !res.data.items?.length) {
        setMenuExtractError('AI could not extract any menu items. Try a clearer photo or PDF.');
        return;
      }
      setMenuExtractResult(res.data);
      // Auto-fill aggregates if user hasn't set them
      setForm(f => ({
        ...f,
        menu_items: res.data.items,
        avg_dish_price: (f.avg_dish_price === '' || f.avg_dish_price == null)
          ? (res.data.avg_dish_price || '')
          : f.avg_dish_price,
        popular_dishes: (!f.popular_dishes || f.popular_dishes.trim() === '')
          ? (res.data.popular_dishes || []).join(', ')
          : f.popular_dishes,
      }));
    } catch (e) {
      setMenuExtractError(e?.response?.data?.detail || e.message || 'Extraction failed');
    } finally {
      setMenuExtracting(false);
    }
  };

  const removeMenuItem = (idx) => {
    setForm(f => {
      const items = (f.menu_items || []).filter((_, i) => i !== idx);
      return { ...f, menu_items: items };
    });
  };

  const updateMenuItem = (idx, key, value) => {
    setForm(f => {
      const items = [...(f.menu_items || [])];
      items[idx] = { ...items[idx], [key]: key === 'selling_price' ? parseFloat(value || 0) : value };
      return { ...f, menu_items: items };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      rating: form.rating === '' ? null : parseFloat(form.rating),
      review_count: form.review_count === '' ? null : parseInt(form.review_count, 10),
      avg_dish_price: form.avg_dish_price === '' ? null : parseFloat(form.avg_dish_price),
      popular_dishes: form.popular_dishes ? form.popular_dishes.split(',').map(s => s.trim()).filter(Boolean) : [],
      menu_items: form.menu_items || [],
    };
    try {
      if (editingId) {
        await axios.patch(`${API}/benchmarks/competitors/${editingId}`, payload);
      } else {
        await axios.post(`${API}/benchmarks/${selectedClient.id}/competitors`, payload);
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      fetchAll();
    } catch (err) {
      alert('Save failed: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete competitor "${c.name}"?`)) return;
    try {
      await axios.delete(`${API}/benchmarks/competitors/${c.id}`);
      fetchAll();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  };

  const handleRefreshGoogle = async (c) => {
    setRefreshingId(c.id);
    try {
      const res = await axios.post(`${API}/benchmarks/competitors/${c.id}/refresh-google`);
      if (!res.data.success) {
        if (res.data.reason === 'not_configured') {
          alert('Google Maps API key not configured yet. Add GOOGLE_PLACES_API_KEY to backend/.env to enable auto-refresh.');
        } else {
          alert(`Refresh failed: ${res.data.message || res.data.reason}`);
        }
      } else {
        fetchAll();
      }
    } catch (e) {
      alert('Refresh failed: ' + e.message);
    } finally {
      setRefreshingId(null);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/benchmarks/${selectedClient.id}/analyze`);
      setAnalysis(res.data.analysis);
      setMyStats(res.data.my_stats);
    } catch (e) {
      alert('Analysis failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setAnalyzing(false);
    }
  };

  if (!selectedClient) return <div className="text-center text-text-secondary py-12">Select a client</div>;

  return (
    <div className="space-y-6" data-testid="benchmarks-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-1">Competitive Intelligence</p>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-2">
            <Target className="h-7 w-7" /> Benchmarks
          </h1>
          <p className="text-sm text-text-secondary mt-1">Track your comp set and get an AI-driven competitive analysis. Search Google Maps directly when adding competitors to auto-fill ratings & reviews.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={startAdd} className="bg-primary hover:bg-primary-hover" data-testid="add-competitor-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Competitor
          </Button>
          <Button onClick={runAnalysis} disabled={competitors.length === 0 || analyzing} variant="outline" data-testid="run-ai-analysis-btn">
            {analyzing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Analysing…</> : <><Sparkles className="h-4 w-4 mr-1" /> Generate AI Analysis</>}
          </Button>
        </div>
      </div>

      {/* Places API status banner — only shown when there's an issue */}
      {placesStatus === 'not_configured' && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 text-xs flex items-start gap-2" data-testid="api-key-reminder">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Google Places API key not set.</strong> Add <code className="bg-amber-100 px-1 rounded">GOOGLE_PLACES_API_KEY</code> to <code className="bg-amber-100 px-1 rounded">backend/.env</code> to unlock typeahead competitor search & auto-refresh. Manual entry still works below.
          </div>
        </div>
      )}
      {placesStatus === 'api_disabled' && (
        <div className="bg-red-50 border border-red-200 text-red-900 rounded-md p-3 text-xs flex items-start gap-2" data-testid="places-api-disabled-banner">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Google Places API (New) is not enabled on this project.</strong>{' '}
            Enable it in the Google Cloud Console for the project tied to this API key, then retry.{' '}
            <a className="underline font-semibold" href="https://console.developers.google.com/apis/api/places.googleapis.com/overview" target="_blank" rel="noreferrer">Open Cloud Console →</a>
            {placesError && <div className="mt-1 text-[10px] opacity-70 break-words">Details: {placesError}</div>}
          </div>
        </div>
      )}
      {placesStatus === 'ok' && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-3 text-xs flex items-start gap-2" data-testid="places-api-ok-banner">
          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div><strong>Google Places API connected.</strong> Use the search box inside <em>Add Competitor</em> to auto-fill ratings, reviews & address from Maps.</div>
        </div>
      )}

      {/* My stats card */}
      {myStats && (
        <div className="bg-white border border-border rounded-md p-4" data-testid="my-stats-card">
          <p className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-2">{selectedClient.name} — Your Stats</p>
          <div className="grid grid-cols-4 gap-3">
            <Stat label="Avg Rating" value={`${(myStats.rating || 0).toFixed(1)} ★`} />
            <Stat label="Reviews" value={(myStats.review_count || 0).toLocaleString()} />
            <Stat label="Avg Dish Price" value={`₹${(myStats.avg_dish_price || 0).toLocaleString()}`} />
            <Stat label="Cover ADR" value={`₹${(myStats.adr || 0).toLocaleString()}`} />
          </div>
        </div>
      )}

      {/* AI Analysis */}
      {analysis && (
        <div className="bg-white border border-border rounded-md p-5" data-testid="ai-analysis-card">
          <p className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-2 flex items-center gap-1">
            <Sparkles className="h-3.5 w-3.5" /> AI Competitive Analysis (GPT-5.2)
          </p>
          <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans text-text-primary" data-testid="ai-analysis-text">{analysis}</pre>
        </div>
      )}

      {/* Price-Positioning Heat-map */}
      <PricePositioningCard
        positioning={positioning}
        loading={loadingPositioning}
      />

      {/* Competitors table */}
      <div className="bg-white border border-border rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-border bg-background-secondary flex items-center justify-between">
          <h3 className="text-base font-semibold text-primary">Competitor Set ({competitors.length})</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-text-secondary"><Loader2 className="h-5 w-5 inline-block animate-spin mr-2" /> Loading…</div>
        ) : competitors.length === 0 ? (
          <div className="p-10 text-center text-text-muted">
            <Target className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-semibold mb-1">No competitors added yet</p>
            <p className="text-xs">Click <strong>Add Competitor</strong> to start tracking. Then click <strong>Generate AI Analysis</strong>.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-primary text-white text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Cuisine</th>
                  <th className="px-4 py-3 text-right">Rating</th>
                  <th className="px-4 py-3 text-right">Reviews</th>
                  <th className="px-4 py-3 text-center">Price</th>
                  <th className="px-4 py-3 text-right">Avg Dish</th>
                  <th className="px-4 py-3 text-left">Popular Dishes</th>
                  <th className="px-4 py-3 text-center">Source</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {competitors.map((c, idx) => (
                  <tr key={c.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-background-secondary'} data-testid={`comp-row-${c.id}`}>
                    <td className="px-4 py-3 text-sm font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary">{c.cuisine || '—'}</td>
                    <td className="px-4 py-3 text-sm text-right">{c.rating ? `${c.rating.toFixed(1)} ★` : '—'}</td>
                    <td className="px-4 py-3 text-sm text-right">{c.review_count ? c.review_count.toLocaleString() : '—'}</td>
                    <td className="px-4 py-3 text-sm text-center font-semibold">{c.price_level || '—'}</td>
                    <td className="px-4 py-3 text-sm text-right">{c.avg_dish_price ? `₹${c.avg_dish_price.toLocaleString()}` : '—'}</td>
                    <td className="px-4 py-3 text-xs text-text-secondary max-w-[200px]">{(c.popular_dishes || []).slice(0, 3).join(', ') || '—'}</td>
                    <td className="px-4 py-3 text-xs text-center">
                      <span className={`px-2 py-0.5 rounded font-mono uppercase ${c.source === 'google_places' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
                        {c.source === 'google_places' ? 'Google' : 'Manual'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleRefreshGoogle(c)}
                          disabled={refreshingId === c.id}
                          title="Refresh from Google Places (requires API key)"
                          className="text-text-secondary hover:bg-primary/10 hover:text-primary rounded p-1"
                          data-testid={`refresh-comp-${c.id}`}
                        >
                          {refreshingId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        </button>
                        <button onClick={() => startEdit(c)} className="text-text-secondary hover:bg-primary/10 hover:text-primary rounded p-1" data-testid={`edit-comp-${c.id}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => handleDelete(c)} className="text-state-error hover:bg-red-50 rounded p-1" data-testid={`delete-comp-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-primary text-white">
              <h2 className="text-lg font-semibold">{editingId ? 'Edit Competitor' : 'Add Competitor'}</h2>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="hover:bg-white/10 rounded p-1"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-3">
              {/* Find on Google (typeahead, auto-fills the form) */}
              {!editingId && (
                <div className="bg-background-secondary border border-border rounded-md p-3" data-testid="places-search-block">
                  <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1 flex items-center gap-1">
                    <Search className="h-3 w-3" /> Find on Google Maps
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQ}
                      onChange={e => setSearchQ(e.target.value)}
                      placeholder="Type a restaurant name (e.g. Britannia & Co, Bandra)"
                      className="w-full border border-border rounded-md px-3 py-2 text-sm pr-9"
                      data-testid="places-search-input"
                    />
                    {searching && <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-2.5 text-text-muted" />}
                  </div>
                  {searchResults.length > 0 && (
                    <ul className="mt-2 border border-border rounded-md bg-white max-h-56 overflow-y-auto divide-y divide-border" data-testid="places-search-results">
                      {searchResults.map(p => (
                        <li
                          key={p.place_id}
                          onClick={() => pickPlace(p)}
                          className="px-3 py-2 cursor-pointer hover:bg-primary/5"
                          data-testid={`place-result-${p.place_id}`}
                        >
                          <div className="text-sm font-medium text-primary">{p.name}</div>
                          <div className="text-[11px] text-text-secondary truncate">{p.address}</div>
                          <div className="text-[11px] text-text-muted mt-0.5">
                            {p.rating ? `${p.rating.toFixed(1)} ★ ` : ''}
                            {p.review_count ? `(${p.review_count.toLocaleString()} reviews) ` : ''}
                            {p.price_level ? `· ${p.price_level}` : ''}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {placesError && searchQ.length >= 2 && !searching && searchResults.length === 0 && (
                    <div className="mt-2 text-[11px] text-red-700">⚠ {placesError}</div>
                  )}
                  <div className="text-[10px] text-text-muted mt-1">Tip: pick a result to auto-fill name, address, rating, reviews & Place ID. You can still edit any field below.</div>
                </div>
              )}

              <Field label="Name *" required value={form.name} onChange={v => setForm({ ...form, name: v })} testid="comp-name" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Cuisine" value={form.cuisine} onChange={v => setForm({ ...form, cuisine: v })} placeholder="e.g. North Indian / Parsi / Continental" testid="comp-cuisine" />
                <SelectField label="Price Level" value={form.price_level} onChange={v => setForm({ ...form, price_level: v })} options={['', ...PRICE_LEVELS]} testid="comp-price-level" />
              </div>
              <Field label="Address" value={form.address} onChange={v => setForm({ ...form, address: v })} testid="comp-address" />
              <div className="grid grid-cols-3 gap-3">
                <Field label="Rating (0–5)" type="number" step="0.1" value={form.rating} onChange={v => setForm({ ...form, rating: v })} testid="comp-rating" />
                <Field label="Review Count" type="number" value={form.review_count} onChange={v => setForm({ ...form, review_count: v })} testid="comp-reviews" />
                <Field label="Avg Dish ₹" type="number" value={form.avg_dish_price} onChange={v => setForm({ ...form, avg_dish_price: v })} testid="comp-avg-dish" />
              </div>
              <Field label="Popular Dishes (comma-separated)" value={form.popular_dishes} onChange={v => setForm({ ...form, popular_dishes: v })} placeholder="Berry Pulav, Caramel Custard" testid="comp-popular" />
              <Field label="Google Place ID (for auto-refresh later)" value={form.google_place_id} onChange={v => setForm({ ...form, google_place_id: v })} placeholder="ChIJ..." testid="comp-place-id" />
              <Field label="Notes" value={form.notes} onChange={v => setForm({ ...form, notes: v })} testid="comp-notes" />

              {/* Menu auto-extract (AI) */}
              <div className="bg-background-secondary border border-border rounded-md p-3" data-testid="menu-extract-block">
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1 flex items-center gap-1">
                  <BookOpen className="h-3 w-3" /> Auto-extract menu from photo / PDF (optional)
                </label>
                <p className="text-[11px] text-text-muted mb-2">
                  Drop a competitor menu (PDF, image, Excel, or CSV) — AI extracts items, prices &amp; categories. Auto-fills <em>avg dish price</em> and <em>popular dishes</em>.
                </p>
                <input
                  ref={menuFileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.csv,.txt"
                  className="hidden"
                  onChange={e => { handleMenuFile(e.target.files?.[0]); e.target.value = ''; }}
                  data-testid="menu-extract-file-input"
                />
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => menuFileInputRef.current?.click()}
                    disabled={menuExtracting}
                    data-testid="menu-extract-btn"
                  >
                    {menuExtracting
                      ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Extracting menu… (15-40s)</>
                      : <><FileUp className="h-3 w-3 mr-1" /> Upload menu file</>}
                  </Button>
                  {(form.menu_items || []).length > 0 && (
                    <span className="text-[11px] text-emerald-700 font-semibold">
                      ✓ {form.menu_items.length} items loaded
                    </span>
                  )}
                </div>

                {menuExtractError && (
                  <div className="mt-2 bg-red-50 border border-red-200 text-red-900 rounded p-2 text-[11px]" data-testid="menu-extract-error">
                    ⚠ {menuExtractError}
                  </div>
                )}

                {menuExtractResult && (
                  <div className="mt-2 text-[11px] text-emerald-900 bg-emerald-50 border border-emerald-200 rounded p-2">
                    Extracted {menuExtractResult.items.length} items · avg ₹{menuExtractResult.avg_dish_price?.toFixed(0)} · confidence {menuExtractResult.overall_confidence}%
                  </div>
                )}

                {(form.menu_items || []).length > 0 && (
                  <div className="mt-3 border border-border rounded bg-white max-h-64 overflow-y-auto" data-testid="menu-items-preview">
                    <table className="w-full text-[11px]">
                      <thead className="bg-background-secondary text-text-secondary uppercase tracking-wider">
                        <tr>
                          <th className="px-2 py-1 text-left">Item</th>
                          <th className="px-2 py-1 text-left">Category</th>
                          <th className="px-2 py-1 text-right">Price ₹</th>
                          <th className="px-2 py-1 w-6"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {form.menu_items.map((it, i) => (
                          <tr key={i} data-testid={`menu-item-row-${i}`}>
                            <td className="px-2 py-1">
                              <input className="w-full bg-transparent border-0 outline-none text-[11px]"
                                value={it.name} onChange={e => updateMenuItem(i, 'name', e.target.value)} />
                            </td>
                            <td className="px-2 py-1 text-text-secondary">{it.sub_category || it.category}</td>
                            <td className="px-2 py-1 text-right">
                              <input type="number" className="w-20 bg-transparent border-0 outline-none text-right text-[11px]"
                                value={it.selling_price} onChange={e => updateMenuItem(i, 'selling_price', e.target.value)} />
                            </td>
                            <td className="px-2 py-1 text-center">
                              <button type="button" onClick={() => removeMenuItem(i)} className="text-text-muted hover:text-state-error" data-testid={`menu-item-remove-${i}`}>
                                <X className="h-3 w-3" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
                <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="save-comp">{editingId ? 'Save Changes' : 'Add Competitor'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const PricePositioningCard = ({ positioning, loading }) => {
  if (loading) {
    return (
      <div className="bg-white border border-border rounded-md p-5" data-testid="positioning-loading">
        <Loader2 className="h-4 w-4 inline-block animate-spin mr-2" />
        <span className="text-xs text-text-secondary">Building price positioning…</span>
      </div>
    );
  }
  if (!positioning || !positioning.matrix?.length) {
    return (
      <div className="bg-white border border-border rounded-md p-5 text-center text-xs text-text-muted" data-testid="positioning-empty">
        <Target className="h-8 w-8 mx-auto mb-2 opacity-30" />
        Add at least one competitor with a menu (via <em>Auto-extract menu</em>) AND build your own menu under <em>Menu</em> to see the price-positioning heat-map. Categories with too few items are hidden automatically.
      </div>
    );
  }

  const { columns, matrix, summary } = positioning;
  const cellBg = (flag) => {
    if (flag === 'you') return 'bg-primary text-accent-gold font-semibold';
    if (flag === 'under_priced') return 'bg-emerald-100 text-emerald-900';
    if (flag === 'over_priced') return 'bg-red-100 text-red-900';
    if (flag === 'parity') return 'bg-slate-100 text-slate-600';
    return 'bg-slate-50 text-text-muted';
  };

  return (
    <div className="bg-white border border-border rounded-md overflow-hidden" data-testid="positioning-card">
      <div className="px-5 py-3 border-b border-border bg-background-secondary">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-base font-semibold text-primary flex items-center gap-2">
              <Target className="h-4 w-4" /> Price-Positioning Heat-Map
            </h3>
            <p className="text-[11px] text-text-secondary mt-0.5">
              Average selling price per category — your restaurant vs the comp set.
            </p>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-text-secondary">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-100 border border-emerald-400 rounded-sm"></span>headroom</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-red-100 border border-red-400 rounded-sm"></span>over-priced</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-slate-100 border border-slate-400 rounded-sm"></span>parity</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="positioning-matrix">
          <thead>
            <tr className="bg-background-secondary">
              <th className="text-left px-3 py-2 text-text-secondary font-semibold uppercase tracking-wider text-[10px] sticky left-0 bg-background-secondary z-10">Category</th>
              {columns.map(col => (
                <th key={col.id} className={`text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold ${col.kind === 'you' ? 'text-accent-gold bg-primary' : 'text-text-secondary'}`}>
                  {col.name.slice(0, 22)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {matrix.map(row => (
              <tr key={row.category} data-testid={`positioning-row-${row.category.replace(/\W+/g, '-')}`}>
                <td className="px-3 py-2 font-semibold text-primary sticky left-0 bg-white z-10">{row.category}</td>
                {row.cells.map(cell => (
                  <td key={cell.col_id} className={`text-right px-3 py-2 ${cellBg(cell.flag)}`}>
                    {cell.avg == null
                      ? <span className="text-text-muted">—</span>
                      : (
                        <>
                          ₹{cell.avg.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          {cell.delta_pct != null && cell.flag !== 'you' && (
                            <div className="text-[9px] opacity-80">
                              {cell.delta_pct > 0 ? '+' : ''}{cell.delta_pct}%
                            </div>
                          )}
                        </>
                      )
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-border bg-background-secondary px-5 py-3 text-[11px] text-text-secondary space-y-1" data-testid="positioning-summary">
        {summary.biggest_headroom && (
          <div>
            <strong className="text-emerald-700">Headroom:</strong> {summary.biggest_headroom.competitor} charges <strong>+{summary.biggest_headroom.delta_pct}%</strong> in <strong>{summary.biggest_headroom.category}</strong> — potential price-lift opportunity.
          </div>
        )}
        {summary.biggest_overprice && (
          <div>
            <strong className="text-red-700">Caution:</strong> {summary.biggest_overprice.competitor} charges <strong>{summary.biggest_overprice.delta_pct}%</strong> in <strong>{summary.biggest_overprice.category}</strong> — you may be losing price-sensitive diners.
          </div>
        )}
        <div className="text-text-muted text-[10px] pt-1">
          {summary.categories_under_priced} categor{summary.categories_under_priced === 1 ? 'y' : 'ies'} under-priced · {summary.categories_over_priced} over-priced · {summary.competitors_analysed} competitors analysed · {summary.own_menu_items} of your own items
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-background-secondary rounded p-3">
    <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">{label}</div>
    <div className="text-lg font-bold text-primary mt-0.5">{value}</div>
  </div>
);

const Field = ({ label, value, onChange, type = 'text', step, required, placeholder, testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">{label}</label>
    <input
      type={type} step={step} required={required} placeholder={placeholder}
      value={value} onChange={e => onChange(e.target.value)}
      className="w-full border border-border rounded-md px-3 py-2 text-sm"
      data-testid={testid}
    />
  </div>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)} className="w-full border border-border rounded-md px-3 py-2 text-sm" data-testid={testid}>
      {options.map(o => <option key={o} value={o}>{o || '—'}</option>)}
    </select>
  </div>
);

export default Benchmarks;
